import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import { LoadingButton } from '@mui/lab'
import Box from '@mui/material/Box'
import OtpInput from 'react-otp-input'
import { AuthFormBox } from './__styled'
import { Input } from '@/components'
import { useTheme } from '@mui/material'
import { validateEmail } from '@/utils/helper'

interface FormProps {
  error: string
  loading: boolean
  user: string
  onSubmit: (code: string) => void
  onResend: () => void
}

export default function SignUpForm3({ error, loading, user, onSubmit, onResend }: FormProps) {
  const [code, setCode] = useState('') // Email or Phone number
  const [time, setTime] = useState(60)
  const theme = useTheme()

  useEffect(() => {
    const timer = setInterval(() => {
      setTime((time) => time - 1)
    }, 1000)
    return () => {
      clearInterval(timer)
    }
  }, [])

  const onAgree = () => {
    onSubmit(code)
  }

  const onResendCode = () => {
    setTime(60)
    onResend()
  }

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100%',
        width: '100%',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        backgroundColor: '#ffffff',
        p: { xs: 3, sm: 4, md: 6 },
      }}
    >
      <Box sx={{ width: '100%', maxWidth: '440px' }}>
        {/* Header */}
        <Box mb={4}>
          <Typography
            variant='h4'
            fontWeight={700}
            color='#1a1a1a'
            mb={1}
            sx={{ fontSize: { xs: '28px', md: '32px' } }}
          >
            Verification Code
          </Typography>
          <Typography variant='body1' color='#666666' lineHeight={1.6}>
            We have sent a verification code to your{' '}
            {validateEmail(user) ? 'email address' : 'phone number'}{' '}
            <Typography component='span' fontWeight={600} color='#1a1a1a'>
              {user}
            </Typography>
          </Typography>
        </Box>

        {/* Form */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {/* OTP Input */}
          <Box width='100%'>
            <OtpInput
              value={code}
              onChange={setCode}
              numInputs={6}
              inputStyle={{
                width: '100%',
                height: '52px',
                borderRadius: '8px',
                fontSize: 24,
                fontWeight: 600,
                border: '2px solid #e0e0e0',
                transition: 'all 0.2s ease',
              }}
              containerStyle={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: '8px',
              }}
              renderInput={(props) => (
                <input
                  {...props}
                  style={{
                    ...props.style,
                    outline: 'none',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#1a1a1a'
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e0e0e0'
                  }}
                />
              )}
            />
          </Box>

          {/* Error Message */}
          {!loading && !!error && (
            <Box
              sx={{
                p: 1.5,
                backgroundColor: '#fee',
                borderRadius: '8px',
                border: '1px solid #fcc',
              }}
            >
              <Typography variant='body2' color='error.main' fontSize='14px'>
                {error}
              </Typography>
            </Box>
          )}

          {/* Resend Code */}
          <Box display='flex' justifyContent='center' mt={1}>
            {time > 0 ? (
              <Typography variant='body2' color='#666666'>
                Resend code in <strong>{time}</strong> seconds
              </Typography>
            ) : (
              <Button
                variant='text'
                onClick={onResendCode}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  color: '#1a1a1a',
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                  },
                }}
              >
                Resend code
              </Button>
            )}
          </Box>

          {/* Continue Button */}
          <LoadingButton
            variant='contained'
            size='large'
            fullWidth
            disabled={code.length < 6}
            onClick={onAgree}
            loading={loading}
            sx={{
              backgroundColor: '#1a1a1a',
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '16px',
              py: 1.5,
              mt: 1,
              '&:hover': {
                backgroundColor: '#333333',
              },
              '&:disabled': {
                backgroundColor: '#e0e0e0',
                color: '#999999',
              },
            }}
          >
            Continue
          </LoadingButton>

          {/* Sign In Link */}
          <Box display='flex' justifyContent='center' alignItems='center' gap={0.5} mt={1}>
            <Typography variant='body2' color='#666666'>
              Got an account?
            </Typography>
            <Link to='/login' style={{ textDecoration: 'none' }}>
              <Typography
                variant='body2'
                sx={{
                  color: '#1a1a1a',
                  fontWeight: 600,
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                Sign In
              </Typography>
            </Link>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
